import { ApiError } from '@/lib/api';

export function getSignatureErrorMessage(error: unknown, translate: (key: string, fallback: string) => string = (_key, fallback) => fallback): string {
  if (error instanceof ApiError) {
    let body: unknown = error.data;
    if (typeof body === 'string') {
      try { body = JSON.parse(body); } catch { body = undefined; }
    }
    const code = body && typeof body === 'object'
      ? ('code' in body ? body.code : 'error' in body ? body.error : undefined)
      : undefined;

    // Older proxies promoted DMS validation bodies without statusCode to 500.
    // This explicit body still identifies a rejection before the signed action.
    if (code === 'VALIDATION_ERROR' || code === 'Validation error') {
      return translate('validation', 'One or more signing fields are invalid. Check the control code format and the other required fields. No signature was recorded.');
    }

    if (code === 'SIGNER_MISMATCH') {
      return translate('signerMismatch', 'Use the username of the account currently signed in. No signature was recorded.');
    }
    if (code === 'ACTOR_REQUIRED') {
      return translate('actorRequired', 'Your session could not be verified. Sign in again before retrying this action.');
    }
    if (error.status === 401 && body && typeof body === 'object'
      && 'msg' in body && body.msg === 'No V4 Session') {
      return translate('sessionExpired', 'Your session has expired. Keep this dialog open, sign in in another tab, then retry signing. No signature was recorded.');
    }
    if (error.status === 401) {
      return translate('credentials', 'Your signature credentials were not accepted. Check the username and current ATEK password. No signature was recorded.');
    }
    if (code === 'account_locked') {
      return translate('accountLocked', 'Your account is locked. Contact an administrator to unlock it before signing again.');
    }
    if (error.status === 429) {
      return translate('rateLimit', 'Too many signing attempts. Wait before trying again; contact an administrator if your account is locked.');
    }
    if (code === 'SEGREGATION_OF_DUTIES') {
      return translate('segregationOfDuties', 'A different authorized reviewer must approve this document. You cannot approve your own document. Once approved, you can make it effective.');
    }
    if (error.status === 403) {
      return translate('permission', 'Your account does not have permission to sign this action. Contact your quality manager.');
    }
    if (code === 'CONTROL_CODE_EXISTS') {
      return translate('duplicateCode', 'That control code is already in use. Enter a different control code.');
    }
    if (error.status === 409) {
      return translate('stateConflict', 'The document state or control code conflicts with this action. Refresh the document and check its current status before signing again.');
    }
    if (error.status === 404) {
      return translate('unavailable', 'This document is no longer available to your account. Close this dialog and refresh the register.');
    }
    if (error.status === 400 || error.status === 422) {
      return translate('requiredFields', 'Check the required fields and signature meaning before trying again.');
    }
  }
  // A timeout/server failure can occur after a commit. Never claim the action
  // was not saved or automatically retry a permanent signed transition.
  return translate('uncertainOutcome', 'We could not confirm whether your signature was recorded. Refresh the document before trying again to avoid signing twice.');
}
